import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@clerk/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { VIDEO_CATS, STRINGS } from "@/lib/i18n";

const catLabel = (c) => STRINGS.en[`vcat.${c}`];
const selectCls = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export default function VideoManager({ videos, setVideos }) {
  const { getToken } = useAuth();
  const [form, setForm] = useState({ title: "", url: "", category: "other" });
  const [busy, setBusy] = useState(false);
  const auth = async () => ({ headers: { Authorization: `Bearer ${await getToken()}` } });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/videos", form, await auth());
      setVideos((v) => [data, ...v]);
      setForm({ title: "", url: "", category: form.category });
      toast.success("Video added to the website");
    } catch (err) {
      toast.error(errMsg(err, "Could not add the video"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this video from the website?")) return;
    try {
      await api.delete(`/admin/videos/${id}`, await auth());
      setVideos((v) => v.filter((x) => x.id !== id));
      toast.success("Video removed");
    } catch (err) {
      toast.error(errMsg(err, "Could not remove the video"));
    }
  };

  const changeCat = async (id, category) => {
    try {
      await api.patch(`/admin/videos/${id}`, { category }, await auth());
      setVideos((v) => v.map((x) => (x.id === id ? { ...x, category } : x)));
      toast.success(`Moved to ${catLabel(category)}`);
    } catch (err) {
      toast.error(errMsg(err, "Could not change the topic"));
    }
  };

  if (!videos) return <p className="text-[#57645A]">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="video-manager">
      <form onSubmit={add} className="bg-white border border-parchment-300 rounded-2xl p-5 grid sm:grid-cols-[1fr_1.3fr_0.7fr_auto] gap-3 items-end">
        <div className="space-y-1.5"><Label>Video title</Label><Input required minLength={2} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Morning breathing practice" data-testid="video-title-input" /></div>
        <div className="space-y-1.5"><Label>YouTube link</Label><Input required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://youtu.be/..." data-testid="video-url-input" /></div>
        <div className="space-y-1.5"><Label>Topic</Label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={selectCls} data-testid="video-category-select">
            {VIDEO_CATS.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
          </select>
        </div>
        <button disabled={busy} className="flex items-center justify-center gap-2 bg-forest-800 hover:bg-forest-700 text-parchment font-semibold px-6 py-2.5 rounded-full disabled:opacity-60" data-testid="video-add-button">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add video
        </button>
      </form>
      {videos.length === 0 ? (
        <div className="bg-white border border-parchment-300 rounded-2xl p-10 text-center text-[#57645A]" data-testid="videos-empty">No videos yet. Add a YouTube link above and it appears on the home page.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="bg-white border border-parchment-300 rounded-2xl overflow-hidden" data-testid={`admin-video-${v.id}`}>
              <img src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full aspect-video object-cover" />
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-forest-800 text-sm truncate">{v.title}</p>
                  <button onClick={() => remove(v.id)} className="p-2 rounded-full text-red-700 hover:bg-red-50" aria-label="Remove video" data-testid={`video-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <select value={v.category || "other"} onChange={(e) => changeCat(v.id, e.target.value)} className={selectCls} data-testid={`video-category-change-${v.id}`}>
                  {VIDEO_CATS.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
